import { RequestedData } from "../flow/interstitials/requested_data/types";

export enum IntegrationCategory {
  HRIS = "hris",
  ATS = "ats",
  ACCOUNTING = "accounting",
  TICKETING = "ticketing",
  CRM = "crm",
  MKTG = "mktg",
  FILESTORAGE = "filestorage",
  DATAWAREHOUSE = "datawarehouse",
}

export type InitiateLinkInfo = {
  integrations: Integration[];
  custom_integrations: CustomIntegration[];
  should_hide_merge_logo: boolean;
  organization: Organization;
  linked_account_category_map: Record<IntegrationCategory, LinkedAccount>;
  is_pre_selected_integration: boolean;
  is_multi_category: boolean;
  show_csv_upload: boolean;
};
/**
 * Keep in sync with merge.integrations.constants.PATH_TYPES
 */
export enum IntegrationSetupStepPathType {
  SCRAPER = "SCRAPER",
  SCRAPER_MFA = "SCRAPER_MFA",
  SCRAPER_SECURITY_QUESTION = "SCRAPER_SECURITY_QUESTION",
  MANUAL_LINKING = "MANUAL_LINKING",
  PARTNERSHIP = "PARTNERSHIP",
}

export enum IntegrationSetupChecklistItemIcon {
  WIFI = "WIFI",
  FILE_TEXT = "FILE_TEXT",
  CLIPBOARD_EDIT = "CLIPBOARD_EDIT",
}

export enum IntegrationSetupChecklistItemStatus {
  INCOMPLETE = "INCOMPLETE",
  COMPLETE = "COMPLETE",
}

export type IntegrationSetupChecklistItem = {
  id: string;
  title: string;
  order_in_checklist: number;
  icon: IntegrationSetupChecklistItemIcon;
  steps: IntegrationSetupStep[];
  status: IntegrationSetupChecklistItemStatus;
};

export enum AuthType {
  OAUTH1 = "OAUTH1",
  OAUTH2 = "OAUTH2",
  BASIC = "BASIC",
  BASIC_WITH_TOKEN_EXCHANGE = "BASIC_WITH_TOKEN_EXCHANGE",
  SCRAPER = "SCRAPER",
  SFTP = "SFTP",
}

export type IntegrationSetupStepPath = {
  id: string;
  auth_type: AuthType;
  custom_auth_choice_text: string;
  steps: IntegrationSetupStep[];
  checklist_items: IntegrationSetupChecklistItem[];
  should_show_requested_data_on_link: boolean;
  checklist_title: string | undefined;
};

export type IntegrationSetupStepConfig = {
  initial_setup_set: IntegrationSetupStepPathType;
  setup_step_sets: SetupStepSets;
};

export type SetupStepSets = {
  [step_sets_type in IntegrationSetupStepPathType]?: IntegrationSetupStepPath[];
};

export type SyncConnection = {
  id: string;
  schema_id: string;
};

export type WebUrlSchema = {
  id: string;
  scheme?: string;
  subdomain: string;
  second_level_domain: string;
  top_level_domain: string;
  description?: string;
};

export type Integration = {
  id: string;
  name: string;
  abbreviated_name?: string;
  categories: IntegrationCategory[];
  image: string;
  color?: string;
  oauth_authorize_url?: string;
  step_config: IntegrationSetupStepConfig;
  square_image: string;
  white_image: string;
  is_custom: false;
  is_on_prem_integration: false;
  is_csv: false;
  base_api_url?: string;
  help_center_auth_guide_url?: string;
  slug: string;
  web_url_schemas?: WebUrlSchema[];
  selective_sync_availability?: Record<string, boolean>;
  has_sandbox_link_choice_step_option?: boolean;
};

export enum AuthOptionType {
  SECRETS = "Secrets",
  OAUTH2 = "OAuth2",
  NONE = "None",
}

export enum SecretFieldType {
  TEXT = "TEXT",
  SELECT = "SELECT",
}

export type SecretOption = {
  label: string;
  value: string;
  default?: boolean;
}

export type SecretSchema = {
  name: string;
  description: string;
  human_readable_name?: string;
  prefix?: string;
  suffix?: string;
  field_type?: SecretFieldType;
  options?: SecretOption[];
  optional?: boolean;
}

export type BaseAuthWithSecrets = {
  name: string;
  description?: string;
  registered_user_secrets: SecretSchema[];
  organization_secrets: SecretSchema[];
}

export type SecretsAuth = BaseAuthWithSecrets & {
  type: AuthOptionType.SECRETS;
}

export type OAuth2Auth = BaseAuthWithSecrets & {
  type: AuthOptionType.OAUTH2;
  oauth_config: OAuth2Config;
  // Public-facing notice rendered on the OAuth login screen before the user
  // authorizes (e.g. the Google Limited Use disclosure). Distinct from
  // `description` (setup instructions shown on the secrets step) — this always
  // renders on the OAuth screen, regardless of registered_user_secrets.
  oauth_notice?: string;
}

export type NoAuth = {
  type: AuthOptionType.NONE;
  name: string;
}

export type OAuth2Config = {
  authorization_url: string;
  token_url: string;
  scope: string;
}


export type AuthOption = SecretsAuth | NoAuth | OAuth2Auth;

export type Connector = {
  id: string;
  name: string;
  slug: string;
  logo_url: string;
  auth_options: AuthOption[];
}

export type CustomIntegration = {
  id: string;
  name: string;
  abbreviated_name?: string;
  image: string;
  color?: string;
  active: boolean;
  category: IntegrationCategory;
  steps: IntegrationSetupStep[];
  square_image: undefined;
  is_custom: true;
  is_on_prem_integration: false;
  is_csv: false;
  base_api_url?: string;
  web_url_schemas?: WebUrlSchema[];
  help_center_auth_guide_url?: string;
  slug?: string;
};

export type IntegrationOrCustomIntegration = Integration | CustomIntegration;

export enum IntegrationSetupStepType {
  STEP_TYPE_USERNAME_PASSWORD = "STEP_TYPE_USERNAME_PASSWORD",
  STEP_TYPE_CUSTOM_DOMAIN = "STEP_TYPE_CUSTOM_DOMAIN",
  STEP_TYPE_SECRETS = "STEP_TYPE_SECRETS",
  STEP_TYPE_CUSTOM_PATH = "STEP_TYPE_CUSTOM_PATH",
  STEP_TYPE_API_KEY = "STEP_TYPE_API_KEY",
  STEP_TYPE_SERVICE_ACCOUNT = "STEP_TYPE_SERVICE_ACCOUNT",
  STEP_TYPE_SFTP_CREDENTIALS = "STEP_TYPE_SFTP_CREDENTIALS",
  STEP_TYPE_SFTP_CREDENTIALS_V2 = "STEP_TYPE_SFTP_CREDENTIALS_V2",
  STEP_TYPE_SFTP_REQUIRED_FIELDS = "STEP_TYPE_SFTP_REQUIRED_FIELDS",
  STEP_TYPE_OAUTH = "STEP_TYPE_OAUTH",
  STEP_TYPE_OAUTH_CLIENT_CREDENTIALS = "STEP_TYPE_OAUTH_CLIENT_CREDENTIALS",
  STEP_TYPE_OVERRIDE_OAUTH_TOKEN_URL_AND_REFRESH_TOKEN = "STEP_TYPE_OVERRIDE_OAUTH_TOKEN_URL_AND_REFRESH_TOKEN",
  STEP_TYPE_ADDITIONAL_AUTH = "STEP_TYPE_ADDITIONAL_AUTH",
  STEP_TYPE_TEXT_ONLY = "STEP_TYPE_TEXT_ONLY",
  STEP_TYPE_MFA_CODE = "STEP_TYPE_MFA_CODE",
  STEP_TYPE_BASE_URL = "STEP_TYPE_OVERRIDE_BASE_API_URL",
  STEP_TYPE_URL_PARSER = "STEP_TYPE_URL_PARSER",
  STEP_TYPE_ORG_CONSENT = "STEP_TYPE_ORG_CONSENT",
  STEP_TYPE_ORG_PURCHASE = "STEP_TYPE_ORG_PURCHASE",
  STEP_TYPE_CHOICE = "STEP_TYPE_CHOICE",
  STEP_TYPE_TRELLO_OAUTH1 = "STEP_TYPE_TRELLO_OAUTH1",
  STEP_TYPE_DEPRECATION_NOTICE = "STEP_TYPE_DEPRECATION_NOTICE",
  STEP_TYPE_WHITELIST_IP_ADDRESS = "STEP_TYPE_WHITELIST_IP_ADDRESS",
}

export enum IntegrationSetupStepInputType {
  INPUT_TYPE_TEXT = "INPUT_TYPE_TEXT",
  INPUT_TYPE_FILE = "INPUT_TYPE_FILE",
}

export type Organization = {
  id: string;
  name: string;
  logo: string;
  should_show_requested_data_in_linking_flow: boolean;
  show_selective_sync?: boolean;
  show_end_user_sandbox_config?: boolean;
  allow_end_user_to_link_with_validation_errors?: boolean;
  merge_link_error_remediation_enabled?: boolean;
};

export interface IntegrationSetupStep extends IntegrationSetupStepBase {
  input_prefix?: string;
  input_suffix?: string;
  step_type: IntegrationSetupStepType;
}

export type IntegrationSetupStepBase = {
  additional_auth_field_keys?: any;
  id: string;
  step_number: number;
  description: string;
  title: string;
  additional_auth_display_names?: Record<string, string>;
  help_guide_url?: string;
  input_placeholder?: string;
  confirm_message?: string;
  button_text?: string;
  sftp_credential_fields?: SftpCredentialFields;
  input_validation_regex?: string;
  input_validation_error?: string;
};

export interface EndUser {
  id?: string;
  origin_id?: string;
  organization_name?: string;
  email_address?: string;
}

export interface LinkedAccount {
  end_user: EndUser;
  is_complete: boolean;
  public_token: string;
  id: string;
  integration: Integration;
  end_user_organization_name: string;
  end_user_email_address: string;
  is_csv_upload: boolean;
  can_map_custom_fields: boolean;
  is_relink_needed?: boolean;
  category?: string;
  organization?: Organization;
  auth_type?: AuthType;
}

export interface LinkedAccountWithOrganization extends LinkedAccount {
  organization: Organization;
}

// ReadOnlyFields are used for Service Account Setup

/**
 * Example value:
 * ```
 * [
 *   ["Email address", "org.account99@intg.merge.dev"],
 *   ["First name", "Integration"],
 *   ["Last name", "Service Account"],
 * ]
 * ```
 */
export type ReadOnlyFields = [string, string | undefined | null][];

export type ServiceAccountFields = ReadOnlyFields;

export interface ServiceAccountFieldsResponse {
  service_account_fields: ServiceAccountFields;
  credentials_regenerated_post_linking_failure?: boolean;
}

// CredentialTableFields are used for SFTP and anything else requiring the IntegrationCredentialsTable

export enum CredentialTableFieldType {
  FILE_DOWNLOAD_BUTTON = "FILE_DOWNLOAD_BUTTON",
  // The default: if the field is not an object, it will just be rendered
  // as text (unless it's null or undefined, in which case nothing will be rendered)
  TEXT_INPUT = "TEXT_INPUT",
  TEXT = "TEXT",
}
export interface CredentialTableFieldDownloadButtonProps {
  field_type: CredentialTableFieldType.FILE_DOWNLOAD_BUTTON;
  file_contents: string;
  filename: string;
}
export interface CredentialTableTextInputProps {
  field_type: CredentialTableFieldType.TEXT_INPUT;
  field_value: string;
  field_key: CredentialTableTextInputFieldKey;
}

export enum CredentialTableTextInputFieldKey {
  pgpPublicKey = "PGP public key",
  publicKey = "Public key",
}

export type CredentialTableFieldValue =
  | CredentialTableFieldDownloadButtonProps
  | CredentialTableTextInputProps
  | string
  | undefined
  | null;

/**
 * Example value:
 * ```
 * [
 *   ["Username", "hayleyye"],
 *   ["Host", "sftp.example.com"],
 *   ["Port", "22"],
 *   ["Path", "/"],
 *   [
 *     "Public Key",
 *     {
 *       fieldType: CredentialTableFieldType.FILE_DOWNLOAD_BUTTON,
 *       fileContents: "ssh-rsa AAAbcdefg....",
 *       filename: "hibob-sftp-public-key.pem",
 *     },
 *   ],
 * ]
 * ```
 */
type CredentialTableFieldTuple = [string, CredentialTableFieldValue]; // [key, value] tuple
export type CredentialTableFields = CredentialTableFieldTuple[];
export type SftpCredentialFields = CredentialTableFields;

export interface SftpCredentialFieldsResponse {
  sftp_credential_fields: SftpCredentialFields;
}

export interface ReportFields {
  required_fields: string[];
  supported_fields: string[];
}

export enum ScraperExecutionStatus {
  RUNNING = "RUNNING",
  WAITING_ON_INPUT = "WAITING_ON_INPUT",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export interface ScraperExecutionWithIO {
  id: string;
  is_io_enabled: boolean;
  created_at: string;
  finished_at: string | undefined;
  is_test_run: boolean;
  status: ScraperExecutionStatus;
  integration_setup_step_type?: IntegrationSetupStepType;
  prompt?: string;
  result?: LinkingFlowSetupResult;
  new_step_path_id?: string;
  validation_errors?: ValidationErrors;
}

export enum LinkingFlowSetupResult {
  SUCCESS = "SUCCESS",
  FIX_INPUT = "FIX_INPUT",
  UNRECOGNIZED_URL = "UNRECOGNIZED_URL",
  FALLBACK_TO_MANUAL = "FALLBACK_TO_MANUAL",
  TRY_AGAIN_LATER = "TRY_AGAIN_LATER",
  EXPIRED_TOKEN = "EXPIRED_TOKEN",
  ADD_CUSTOM_FIELDS = "ADD_CUSTOM_FIELDS",
  REQUEST_MFA_CODE = "REQUEST_MFA_CODE",
  ASK_SECURITY_QUESTION = "ASK_SECURITY_QUESTION",
  MISSING_ADMIN_PERMISSION = "MISSING_ADMIN_PERMISSION",
  SCRAPER_WITH_IO_RUNNING = "SCRAPER_WITH_IO_RUNNING",
  PREEXISTING_LINKED_ACCOUNT_BLOCKED = "PREEXISTING_LINKED_ACCOUNT_BLOCKED",
  SUCCESS_BUT_PAUSED_DUE_TO_BILLING = "SUCCESS_BUT_PAUSED_DUE_TO_BILLING",
  ADD_CUSTOM_FIELDS_V2 = "ADD_CUSTOM_FIELDS_V2",
  SERVICE_ACCOUNT_SETUP_SUCCESS = "SERVICE_ACCOUNT_SETUP_SUCCESS",
  VALIDATION_BP_ERROR = "VALIDATION_BP_ERROR",
}

export enum InterstitialType {
  CUSTOM_FIELDS,
  CUSTOM_FIELDS_V2,
  EXPIRED_TOKEN,
  LOADING,
  ERROR,
  SUCCESS,
  TRY_AGAIN_LATER,
  MISSING_ADMIN_PERMISSION,
  DEMO_OAUTH,
  PREEXISTING_LINKED_ACCOUNT_BLOCKED,
  SUCCESS_BUT_PAUSED_DUE_TO_BILLING,
  SERVICE_ACCOUNT_SETUP_SUCCESS,
}

export enum NonStepPageType {
  AUTH_CHOICE = "AUTH_CHOICE",
  REQUESTED_DATA_PAGE = "REQUESTED_DATA_PAGE",
  SELECTIVE_SYNC_PAGE = "SELECTIVE_SYNC_PAGE",
  LINKED_ACCOUNT_TYPE_PAGE = "LINKED_ACCOUNT_TYPE_PAGE",
}

export type PageType = InterstitialType | NonStepPageType;

export type ValidationErrors = ValidationError[];

export type FieldName = {
  field_name: string;
};

export type ValidationError = {
  detail: string;
  problem_type?: string;
  error_code?: number;
  readable_model_name?: string;
  field_names?: FieldName[];
  [key: string]: any;
};

export type ValidationSuccess = {
  readable_model_name?: string;
  field_names?: FieldName[];
};

/** External-facing, optional second parameter for the onSuccess handler. */
export type AdditionalSuccessfulLinkInfo = {
  isTokenForOtherPreexistingLinkedAccount?: boolean;
};

export type MergeFilePickerAuthenticationData = {
  integrationSlug: string;
};

export type MergeFileStorageData = {
  outputType?: FilePickerObjectType;
  // informs on which integration this file was picked from
  authenticationData?: MergeFilePickerAuthenticationData;
  id: string;
  name?: string;
  description?: string;
  // url to access the file/folder
  url?: string;
  // updated_at is a datetime string
  updatedAt?: string;
  // size in bytes
  size?: number;
  // for files they can have a mime type (essentially file_type)
  mime_type?: string;
};

export enum FilePickerObjectType {
  FILE = "FILE",
  FOLDER = "FOLDER",
  DRIVE = "DRIVE",
  SITE = "SITE",
}

export type FilePickerAllowedTypes = Exclude<FilePickerObjectType, FilePickerObjectType.SITE>;

export type FilePickerConfig = {
  onSubmit: (returnVal: Array<MergeFileStorageData>) => void;
  types?: FilePickerAllowedTypes[];
  allowMultiSelect?: boolean;
};

export enum ConditionType {
  BOOLEAN = "BOOLEAN",
  DATE = "DATE",
  DATE_TIME = "DATE_TIME",
  INTEGER = "INTEGER",
  FLOAT = "FLOAT",
  STRING = "STRING",
}

export enum ConditionOperator {
  EQUALS = "EQUALS",
  NOT_EQUALS = "NOT_EQUALS",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_OR_EQUAL_TO = "GREATER_THAN_OR_EQUAL_TO",
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL_TO = "LESS_THAN_OR_EQUAL_TO",
  IS_ONE_OF = "IS_ONE_OF",
  HAS_ALL_OF = "HAS_ALL_OF",
  HAS_NONE_OF = "HAS_NONE_OF",
  CONTAINS = "CONTAINS",
}

export interface ConditionOperatorSchema {
  operator: string;
  is_unique?: boolean;
}

export type ConditionSchema = {
  id: string;
  common_model: string;
  native_name: string | null;
  field_name: string | null;
  is_unique: boolean;
  condition_type: ConditionType;
  operators: ConditionOperatorSchema[];
  choices: string[] | null;
};

export type ConditionSchemasByID = {
  [conditionSchemaID: string]: ConditionSchema;
};

export interface SelectiveSyncCondition {
  condition_schema_id: string;
  common_model: string;
  native_name: string | null;
  field_name: string | null;
  operator: string | undefined;
  value: any;
}

export type SelectiveSyncConfiguration = {
  linked_account_conditions: SelectiveSyncCondition[];
};

export type ConditionsByName = {
  [commonModelName: string]: SelectiveSyncCondition[];
};

export type SelectiveSyncConditionRequest = Pick<
  SelectiveSyncCondition,
  "condition_schema_id" | "operator" | "value"
>;

export type SelectiveSyncConfigurationRequest = {
  linked_account_conditions: Array<SelectiveSyncConditionRequest>;
};

export type SelectiveSyncConfigurationListRequest = Array<SelectiveSyncConfigurationRequest>;

export enum EndUserAccountType {
  PRODUCTION = "END_USER_PRODUCTION_ACCOUNT",
  SANDBOX = "END_USER_SANDBOX_ACCOUNT",
}

export enum FieldMappingConfigurationSource {
  LINKED_ACCOUNT = "LINKED_ACCOUNT",
  ORGANIZATION = "ORGANIZATION",
}

export type FieldMappingPatchRequestBody = {
  id?: string;
  linked_account_id?: string;
  common_model_name: string;
  field_key: string;
  field_traversal_path: string[];
  origin_type: string;
  field_description?: string;
  configured_by: FieldMappingConfigurationSource;
  create_for_organization?: boolean;
  display_name?: string;
  field_mapping_target_id?: string;
  field_mapping_target?: FieldMappingTarget;
  api_endpoint_id?: string;
  weight?: number;
  jmes_path?: string;
  is_marked_for_deletion?: boolean;
};

export type FieldMappingTarget = {
  id?: string;
  category: string;
  common_model_name: string;
  field_key: string;
  field_description?: string;
  weight?: number;
  is_mappable_in_link?: boolean;
};

export type FieldMappingSource = {
  category: string;
  common_model_name: string;
  api_endpoint_id: string;
  api_endpoint_method: string;
  api_endpoint_path: string;
  field_traversal_path: string[];
  displayName: string;
  type?: string;
  value?: string;
};

export type FieldMappingCommonModelConfiguration = {
  id: string;
  common_model_name: string;
  allow_user_created_field_mapping_targets: boolean;
};

export type FieldMappingConfigurationMeta = {
  key: string;
  field_mapping_array: FieldMappingTarget[];
  field_mapping_common_model_configuration_array: FieldMappingCommonModelConfiguration[];
};

export type FieldMappingAvailablePath = {
  [pathName: string]: FieldMappingTarget;
};

export type FieldMappingAvailableOptions = {
  [commonModelName: string]: FieldMappingAvailablePath;
};

export type FieldMappingAPIEndpointToFieldsMap = {
  [endpointID: string]: { [path: string]: string };
};

export type AvailableFieldMappings = {
  [commonModelName: string]: AvailableFieldMapping;
};

export type AvailableFieldMapping = {
  [endpointID: string]: { [path: string]: FieldMappingFieldMappingInstanceDict };
};

export type FieldMappingFieldMappingInstanceDict = {
  api_endpoint_id: string;
  display_name: string;
  traversal_path: string;
  type: string;
  value: string;
  api_endpoint_method: string;
  api_endpoint_path: string;
};

export enum ErrorRemediationScreenEnum {
  INITIAL_INTERSTITIAL = "INITIAL_INTERSTITIAL",
  VALIDATION_ERRORS = "VALIDATION_ERRORS",
  ALREADY_INTEGRATED = "ALREADY_INTEGRATED",
}


export enum CommonModelScopeAction {
  ENABLED_ACTION_READ = "ENABLED_ACTION_READ",
  ENABLED_ACTION_WRITE = "ENABLED_ACTION_WRITE",
}

export type CommonModelScopeField = {
  field_description: string;
  field_format: string | null;
  field_name: string;
  field_type: string;
  is_enabled: boolean;
  is_end_user_configurable: boolean;
  related_to: string | null;
};

export type CommonModelScope = {
  enabled_model_actions: CommonModelScopeAction[];
  end_user_configurable_actions: CommonModelScopeAction[];
  fields: CommonModelScopeField[];
  has_endpoints: boolean;
  is_permanently_disabled: boolean;
  model_capabilities: CommonModelScopeAction[];
  model_description: string;
  name: string;
  readable_model_name: string;
};

/**
 * Selective Sync V2 Types
 */

export type ConditionValueType = string | Array<string> | undefined;

export type LinkedAccountCondition = {
  id: string | null;
  condition_schema_id?: string;
  automated_modified_at_filter_id?: string
  common_model: string;
  normalized_key_name: string;
  remote_key_name: string;
  operator: ConditionOperator;
  value: ConditionValueType;
};



export type MergeLinkOrgIntegrationCustomizationData = {
  status: string;
  integration: Integration;
  help_center_authentication_message: string | null;
  help_center_authentication_url: string | null;
  help_center_validation_url: string | null;
};

export type MergeLinkOrganizationCustomizationData = {
  status: string;
  auto_enable_new_integrations?: boolean | null;
  show_merge_footer?: boolean | null;
  show_selective_sync_screen?: boolean | null;
  show_scopes_screen?: boolean | null;
  primary_button_color?: string | null;
  primary_button_text_color?: string | null;
  font?: string | null;
  logo?: string | null;
  show_cobranding?: boolean | null;
  button_border_radius?: string | null;
  selection_button_border_radius?: string | null;
  modal_border_radius?: string | null;
};

export type RegisteredUserFormData = {
  registered_user_secrets: undefined | { [key: string]: string };
}

export type LinkedAccountFormData = {
  apiKey: undefined | string;
  customDomain: undefined | string;
  customPath: undefined | string;
  username: undefined | string;
  password: undefined | string;
  oauthClientID: undefined | string;
  oauthClientSecret: undefined | string;
  oauthRefreshToken: undefined | string;
  overrideOAuthTokenUrl: undefined | string;
  additionalAuthFields: undefined | { [key: string]: string };
  mfaCode: undefined | string;
  baseURL: undefined | string;
  syncConfigurations: undefined | ConditionsByName;
  syncConfigurationsFormatted: undefined | SelectiveSyncConfigurationListRequest;
  endUserAccountType: undefined | EndUserAccountType;
  linkChoiceStepOptionName: undefined | string;
  fullURL: undefined | string;
  linked_account_conditions?: LinkedAccountCondition[]; // Selective Sync V2 Fields
  report_file_ids?: string[];
};

export type NextPagePayloadInfo = {
  enabled_categories_map?: Record<string, string[]>;
  enabled_categories?: string[];
  should_show_tos_footer?: boolean;
  integrations?: Integration[];
  custom_integrations?: CustomIntegration[];
  integration?: Integration;
  connector?: Connector;
  custom_integration?: CustomIntegration;
  selected_category?: IntegrationCategory;
  requested_data?: RequestedData;
  account_type?: EndUserAccountType;
  linked_account_category_map?: Record<IntegrationCategory, LinkedAccount>;
  step_path?: IntegrationSetupStepPath;
  selected_step_path_id?: string;
  linked_account?: LinkedAccountWithOrganization;
  link_was_successful?: boolean;
  authorization_code?: string;
  should_auto_exit?: boolean;
  public_token?: string;
  field_mapping_key?: string;
  field_mapping_mappable_targets?: FieldMappingTarget[];
  available_field_mappings?: AvailableFieldMappings;
  field_mapping_instances?: FieldMappingPatchRequestBody[];
  available_end_user_common_model_mappings?: FieldMappingConfigurationMeta;
  registered_user_form_data?: RegisteredUserFormData;
  linked_account_form_data?: LinkedAccountFormData;
  validation_successes?: ValidationSuccess[];
  validation_errors?: ValidationErrors;
  validation_result?: LinkingFlowSetupResult;
  already_validated_flow?: boolean;
  is_relink_needed?: boolean;
  can_field_mappings_be_refreshed?: boolean;
  can_selective_sync_be_refreshed?: boolean;
  organization?: Organization;
  schemas?: ConditionSchema[];
  linked_account_id?: string;
  scraper_execution_id?: string;
  scraper_execution_results?: ScraperExecutionWithIO;
  from_already_integrated?: boolean;
  serialized_organization_integration_customization?: MergeLinkOrgIntegrationCustomizationData | null;
  serialized_organization_customization?: MergeLinkOrganizationCustomizationData | null;
  third_party_error?: boolean;
  error_message?: string;
  sync_connection?: SyncConnection;
  has_end_user_configurability_data?: boolean;
  serialized_link_choice_step_options?: string[];
  should_call_next_page?: boolean;
  admin_magic_link?: string;
  organization_name?: string;
  is_linked_account_complete?: boolean;
  is_field_mapping_deletion_enabled?: boolean;
  validate_before_s2?: boolean;
};



export type RegisteredUser = {
  id: string;
  name: string;
  origin_id: string;
};


export type IntegrationSetupPayload = {
  connector: Connector;
  auth_option: AuthOption;
  registered_user: RegisteredUser;
  registered_user_form_data?: RegisteredUserFormData;
  should_block_oauth_on_cookies?: boolean;
  serialized_organization_customization?: MergeLinkOrganizationCustomizationData | null;
};

export type NextPageResponse = {
  next_step: LinkingFlowPageEnum;
  linking_flow_payload: NextPagePayloadInfo;
  translated_language_data: { [key: string]: string };
};

/**
 * Identity for a Link session, resolved from the link token. `id` is the
 * globally unique registered user id; `origin_id` is the customer's own
 * identifier for that user and is only unique within their organization.
 */
export type AgentHandlerLinkEndUserDetails = {
  id: string;
  origin_id: string;
  customer_organization_id: string;
  customer_organization_name: string;
};

export type OrganizationMetadataResponse = {
  should_hide_merge_logo: boolean;
};

export enum LinkingFlowActionEnum {
  LINKING_FLOW_OPENED = "LINKING_FLOW_OPENED",
  LINKING_FLOW_CLOSED = "LINKING_FLOW_CLOSED",
}

export enum LinkingFlowPageEnum {
  LINKING_FLOW_INITIAL_STEP = "INITIAL_STEP",
  LINKING_FLOW_EXPIRED_TOKEN = "EXPIRED_TOKEN",
  LINKING_FLOW_INTEGRATION_SELECTION = "INTEGRATION_SELECTION",
  LINKING_FLOW_AUTH_CHOICE = "AUTH_CHOICE",
  LINKING_FLOW_CONNECTOR_SETUP = "CONNECTOR_SETUP",
  LINKING_FLOW_END_USER_ACCOUNT_TYPE = "END_USER_ACCOUNT_TYPE",
  LINKING_FLOW_LINKING_LOADING = "LINKING_LOADING",
  LINKING_FLOW_REQUESTED_DATA = "REQUESTED_DATA",
  LINKING_FLOW_TERMS_OF_SERVICE = "TERMS_OF_SERVICE",
  LINKING_FLOW_SUCCESS = "SUCCESS",
  LINKING_FLOW_DETAILED_ERROR_MESSAGES = "DETAILED_ERROR_MESSAGES",
  LINKING_FLOW_FAILED = "FAILED",
}

export type StateHistoryRecord = {
  page: LinkingFlowPageEnum;
  payload: NextPagePayloadInfo;
  translatedLanguageData: { [key: string]: string };
};

export type EditCommonModelScopeResponse = {
  common_models: CommonModelScope[];
  requested_data: RequestedData;
};

export enum AlreadyIntegratedAction {
  FIX = "FIX",
  VIEW_DATA = "VIEW_DATA",
  UPDATE = "UPDATE",
  SELECTIVE_SYNC = "SELECTIVE_SYNC",
  FIELD_MAPPINGS = "FIELD_MAPPINGS",
  MANUAL_UPLOAD = "MANUAL_UPLOAD",
}
